import React, { useState } from 'react';

import Search from '../../elements/Search';
import Paginator from '../../elements/paginator';

import CRMApi from 'Api/CRM/CRMApi';
import GetResellerResponse from 'Api/CRM/Responses/ResellerResponse';
import DetachElement from 'crm/elements/DetachElement';
import DownloadButton from 'crm/elements/DownloadButton';
import EditButton from 'crm/elements/EditButton';
import ViewButton from 'crm/elements/ViewButton';
import Card from 'crm/elements/common/Card';
import TableHead from 'crm/elements/common/TableHead';
import TableLoader from 'crm/elements/common/TableLoader';
import { PERMISSION } from 'crm/helpers/Permissions';
import useAutoPaginator, { FetchPaginatorParams } from 'crm/hooks/useAutoPaginator';
import { ROLES } from 'crm/services/AdminPolicyService';

type Props = { source?: 'resellers'; relatedId?: never } | { source: 'super_agent'; relatedId: number };

function ResellersList({ source = 'resellers', relatedId }: Readonly<Props>) {
    const isMainSource = source === 'resellers';
    const url = isMainSource ? 'reseller' : `${source}/${relatedId}/resellers`;
    const [queryParams, setQueryParams] = useState<FetchPaginatorParams>();

    const { items, updateItems, isLoading, auto } = useAutoPaginator<GetResellerResponse>(async (page, params) => {
        setQueryParams(params);

        return (await CRMApi.get(`${url}?transformStateAndCountryFields`, { params: { page, ...params } })).data;
    });

    const getLinks = (resellerId: number) => (
        <>
            <ViewButton url={`/resellers/${resellerId}`} permissions={[PERMISSION.VIEW_RESELLER]} />
            {isMainSource ? (
                <EditButton url={`/resellers/${resellerId}/edit`} permissions={[PERMISSION.UPDATE_RESELLER]} />
            ) : (
                <DetachElement
                    url={`/crea/api/${url}`}
                    params={{ reseller: resellerId }}
                    action={updateItems}
                    roles={ROLES.MAIN_ADMINS}
                />
            )}
        </>
    );

    const tableHeader = (
        <>
            <span className="mr-3">{isMainSource ? 'All resellers' : 'Resellers'}</span>
            {isMainSource && (
                <DownloadButton
                    action="resellers-as-csv"
                    title="Export to CSV"
                    permissions={[PERMISSION.DOWNLOAD_RESELLERS]}
                    defaultFilename="resellers.csv"
                    params={queryParams}
                />
            )}
        </>
    );

    return (
        <Card title={tableHeader}>
            <Search {...auto.search} />
            <TableLoader isLoading={isLoading}>
                <table className="table">
                    <TableHead>
                        <th>ID</th>
                        <th>Phone country</th>
                        <th>Phone</th>
                        <th>Name</th>
                        <th>Last name</th>
                        <th>Country</th>
                        <th>State</th>
                        <th>Email</th>
                        <th>Count tickets</th>
                        <th>Sale sum</th>
                        <th>Balance</th>
                        <th>Revenue</th>
                        <th>Reseller debit</th>
                        <div />
                    </TableHead>
                    <tbody>
                        {items.map((reseller) => (
                            <tr key={reseller.id}>
                                <td>{reseller.id}</td>
                                <td>{reseller.phone_country}</td>
                                <td>{reseller.phone}</td>
                                <td>{reseller.name}</td>
                                <td>{reseller.last_name}</td>
                                <td>{reseller.country}</td>
                                <td>{reseller.state}</td>
                                <td>{reseller.email}</td>
                                <td>{reseller.lottery_tickets_count}</td>
                                <td>{reseller.sale_sum}</td>
                                <td>{reseller.balance_formatted}</td>
                                <td>{reseller.revenue}</td>
                                <td>{reseller.reseller_debit_formatted}</td>
                                <td>{getLinks(reseller.id)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

export default ResellersList;
